import { useState } from 'react';
import ReactModal from 'react-modal';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import AutoSizer from 'react-virtualized-auto-sizer';
// API
import { useAuth } from '../../../../../hooks/useAuth';
import { useEnv } from '../../../../../context/env-context';
import { toast } from 'react-toastify';
// icons
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
// utils
import { modalStyle } from '../../../../../utils/modal';
import { MOBILE_DEVICE_DIMENSIONS } from '../../../../../utils/mobileDevices';
import { makeRequest } from '../../../../../utils/makeRequest';
import { getHTMLWrapper } from '../../../../../utils/strings';
// components
import { Spinner } from '../../../../../components/Spinner';
import HTMLEditor from '../../../../../components/HTMLEditor';
import HTMLPreview from '../../../../../components/HTMLPreview';

function ConsumerNotificationModal({ modalOpen, closeModal, consumerId, getNotifications }) {
  const { apiOriginNotifications } = useEnv();
  const { getAccessTokenSilently } = useAuth();

  const [spinner, setSpinner] = useState(false);

  const notificationInitialState = {
    title: '',
    message: '',
    bodyType: 'TEXT',
    body: '',
    htmlBody: '',
    icon: 'GREEN_FREE_TEXT',
  };

  const [notification, setNotification] = useState(notificationInitialState);

  const [preview, setPreview] = useState({
    device: 'small_phone',
    width:
      MOBILE_DEVICE_DIMENSIONS.small_phone.viewport_width *
      MOBILE_DEVICE_DIMENSIONS.small_phone.scale,
    height:
      MOBILE_DEVICE_DIMENSIONS.small_phone.viewport_height *
      MOBILE_DEVICE_DIMENSIONS.small_phone.scale,
  });

  const updateField = (value, fieldName) => {
    setNotification(prev =>
      fieldName === 'bodyType'
        ? { ...prev, htmlBody: '', [fieldName]: value }
        : { ...prev, [fieldName]: value },
    );
  };

  const close = () => {
    setNotification(notificationInitialState);
    closeModal();
  };

  const sendFreeTextNotification = async () => {
    setSpinner(true);
    const token = await getAccessTokenSilently();

    if (!token) {
      return;
    }

    const config = {
      token,
      url: `${apiOriginNotifications}/send_free_text_message`,
      method: 'POST',
      data: JSON.stringify({
        consumerId,
        ...notification,
        htmlBody:
          notification.bodyType !== 'TEXT' ? getHTMLWrapper(notification.htmlBody) : undefined,
      }),
    };

    try {
      await makeRequest(config);

      getNotifications();
      close();

      toast.success('Free text notification has been sent');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSpinner(false);
    }
  };

  const disableButton = () => {
    if (notification.bodyType === 'TEXT') {
      return (
        notification.title.length === 0 ||
        notification.title.length > 50 ||
        notification.message.length === 0 ||
        notification.message.length > 150 ||
        notification.body.length === 0
      );
    }

    return (
      notification.title.length === 0 ||
      notification.title.length > 50 ||
      notification.message.length === 0 ||
      notification.message.length > 150 ||
      notification.htmlBody.length === 0
    );
  };

  return (
    <ReactModal
      isOpen={!!modalOpen}
      style={modalStyle({ width: '1200px', height: '88vh' })}
      shouldCloseOnOverlayClick={false}
      shouldCloseOnEsc={!spinner}
      onRequestClose={close}
    >
      <AutoSizer>
        {({ width }) => (
          <Spinner visible={spinner} overlayWidth={width}>
            <div className="d-flex flex-column pe-2" style={{ width }}>
              <div className="d-flex align-items-center justify-content-between mb-4">
                <h5 className="mb-0">Send Notification</h5>
                <Button className="bo-common-base-button" onClick={close}>
                  <FontAwesomeIcon icon={faXmark} size="lg" color="#000000" />
                </Button>
              </div>
              <div className="d-flex flex-column justify-content-between">
                <div>
                  <Form.Label className="bo-consumer-modal-form-label">
                    Title (20-50 characters):
                  </Form.Label>
                  <Form.Control
                    type="text"
                    className="bo-consumer-modal-form-input"
                    value={notification.title}
                    onChange={e => updateField(e.target.value, 'title')}
                  />
                  <Form.Label className="bo-consumer-modal-form-label mt-2">
                    Message (up to 150 characters):
                  </Form.Label>
                  <Form.Control
                    type="text"
                    className="bo-consumer-modal-form-input"
                    value={notification.message}
                    onChange={e => updateField(e.target.value, 'message')}
                  />
                  <Form.Label className="bo-consumer-modal-form-label">Body Type:</Form.Label>
                  <Form.Select
                    className="bo-consumer-modal-form-input"
                    value={notification.bodyType}
                    onChange={e => {
                      if (e.target.value === 'HTML') {
                        setSpinner(true);
                      }
                      updateField(e.target.value, 'bodyType');
                    }}
                  >
                    <option value="TEXT">Text</option>
                    <option value="HTML">HTML</option>
                  </Form.Select>
                  <Form.Label className="bo-consumer-modal-form-label">
                    {notification.bodyType === 'TEXT'
                      ? 'Body:'
                      : 'Textual Body (for old mobile versions):'}
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    className="bo-consumer-modal-form-input bo-h-60-fixed bo-resize-none"
                    value={notification.body}
                    onChange={e => updateField(e.target.value, 'body')}
                  />
                  {notification.bodyType === 'HTML' && (
                    <div className="d-flex mt-3">
                      <HTMLEditor
                        width={width - 8}
                        preview={preview}
                        onLoadContent={() => setSpinner(false)}
                        onEditorChange={value =>
                          setNotification(prev => ({ ...prev, htmlBody: value }))
                        }
                        value={notification.htmlBody}
                        allowCustomButtons
                      />
                      <HTMLPreview
                        title={notification.title}
                        value={notification.htmlBody}
                        preview={preview}
                        setPreview={setPreview}
                        type="notification"
                      />
                    </div>
                  )}
                  <Form.Label className="bo-common-popover-form-menu-label">Icon Color:</Form.Label>
                  <Form.Select
                    className="bo-common-popover-form-menu-input"
                    value={notification.icon}
                    onChange={e => updateField(e.target.value, 'icon')}
                  >
                    <option value="GREEN_FREE_TEXT">Green</option>
                    <option value="ORANGE_FREE_TEXT">Orange</option>
                    <option value="RED_FREE_TEXT">Red</option>
                  </Form.Select>
                </div>
                <div className="my-3 d-flex justify-content-between">
                  <Button
                    className="bo-common-button"
                    disabled={disableButton()}
                    onClick={sendFreeTextNotification}
                  >
                    Send
                  </Button>
                  <Button className="bo-common-button" onClick={close}>
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          </Spinner>
        )}
      </AutoSizer>
    </ReactModal>
  );
}

export default ConsumerNotificationModal;
